// Display helpers for the Insights tab, kept separate so they're testable.

export const pct = (value) => (value === null || value === undefined ? '—' : `${Math.round(value)}%`);

// How many of an item's handoffs mentioned it. Uses the backend's count, or
// works it out from the percentage if an older backend didn't send one.
export function mentionedCount(item) {
  if (Number.isInteger(item.mentionedCount)) return item.mentionedCount;
  if (item.mentionedPct == null || !item.handoffCount) return 0;
  return Math.round((item.mentionedPct / 100) * item.handoffCount);
}

// An item needs this many handoffs to be ranked: 5, or 10% of the shift's
// handoffs if that's more, so "1 missed of 1" can't top the list. Same rule as
// the backend (checklist_data.rank_threshold).
export const rankThreshold = (handoffCount) => Math.max(5, Math.ceil(0.1 * (handoffCount || 0)));

// The items with `ranked` set: the backend's flag, or the same rule worked out
// here if an older backend didn't send one. If no item has enough handoffs
// (a small shift), every item is ranked.
export function withRanking(items, handoffCount) {
  const list = items || [];
  if (list.every((i) => typeof i.ranked === 'boolean')) return list;
  const threshold = rankThreshold(handoffCount);
  const anyRanked = list.some((i) => i.handoffCount >= threshold);
  return list.map((i) => ({ ...i, ranked: !anyRanked || i.handoffCount >= threshold }));
}

// The most-mentioned and most-missed ranked items, for the key facts. Ties go
// to the item that appears in more handoffs.
export function keyItems(items) {
  const scored = (items || []).filter((i) => i.ranked !== false && i.handoffCount > 0 && i.mentionedPct != null && i.missedPct != null);
  if (scored.length === 0) return null;
  const best = (key) => scored.reduce((a, b) => (b[key] > a[key] || (b[key] === a[key] && b.handoffCount > a.handoffCount) ? b : a));
  return { mentioned: best('mentionedPct'), missed: best('missedPct') };
}

// Ranked items first, each group by % missed, highest first, then by name
// (the backend sorts too; this keeps the order stable).
export function sortByMissed(items) {
  return [...(items || [])].sort((a, b) => (a.ranked === false) - (b.ranked === false)
    || (b.missedPct ?? -1) - (a.missedPct ?? -1) || a.name.localeCompare(b.name));
}
